/**
 * Lazy Logger Service
 * Provides structured logging with lazy loading to avoid circular dependencies
 */

interface LogLevel {
  info: (message: string, meta?: any) => void;
  warn: (message: string, meta?: any) => void;
  error: (message: string, meta?: any) => void;
  debug: (message: string, meta?: any) => void;
}

class LazyLogger implements LogLevel {
  private loggerPromise: Promise<any> | null = null;

  private async getLogger() {
    if (!this.loggerPromise) {
      this.loggerPromise = import('./loggingService').then(module => module.logger);
    }
    return this.loggerPromise;
  }

  async info(message: string, meta?: any) {
    try {
      const logger = await this.getLogger();
      logger.info(message, meta);
    } catch (error) {
      console.log(`[INFO] ${message}`, meta);
    }
  }

  async warn(message: string, meta?: any) {
    try {
      const logger = await this.getLogger();
      logger.warn(message, meta);
    } catch (error) {
      console.warn(`[WARN] ${message}`, meta);
    }
  }

  async error(message: string, meta?: any) {
    try {
      const logger = await this.getLogger();
      logger.error(message, meta);
    } catch (error) {
      console.error(`[ERROR] ${message}`, meta);
    }
  }

  async debug(message: string, meta?: any) {
    try {
      const logger = await this.getLogger();
      logger.debug(message, meta);
    } catch (error) {
      console.debug(`[DEBUG] ${message}`, meta);
    }
  }

  // Synchronous fallback methods for critical paths
  infoSync(message: string, meta?: any) {
    if (import.meta.env.DEV) {
      console.log(`[INFO] ${message}`, meta);
    }
  }

  warnSync(message: string, meta?: any) {
    // Only log warnings in development, or always log critical errors
    if (import.meta.env.DEV || message.includes('critical') || message.includes('CRITICAL')) {
      console.warn(`[WARN] ${message}`, meta);
    }
  }

  errorSync(message: string, meta?: any) {
    // Always log errors, but only in console in dev mode
    // In production, errors should go to Sentry via the async error method
    if (import.meta.env.DEV) {
      console.error(`[ERROR] ${message}`, meta);
    }
    // Try to send to Sentry even in prod
    this.error(message, meta).catch(() => {
      // Silent fail - already logged to console in dev
    });
  }

  debugSync(message: string, meta?: any) {
    if (import.meta.env.DEV) {
      console.debug(`[DEBUG] ${message}`, meta);
    }
  }
}

export const lazyLogger = new LazyLogger();
